'use client'

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useMutation, useQuery } from '@apollo/client'
import { SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import {
  CREATE_PRODUCT_ATTRIBUTE,
  GET_PRODUCT_ATTRIBUTE,
  GET_ATTRIBUTE_GROUPS_FOR_SELECT,
  UPDATE_PRODUCT_ATTRIBUTE,
  DELETE_PRODUCT_ATTRIBUTE,
} from '../productAttributeQueries'
import { ProductAttributeFormValues } from '../types'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import { Icons } from '@/components/shared/icons'
import { useConfirmation } from '@/context/ConfirmationContext'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const formSchema = z.object({
  code: z.string().optional(),
  productAttributeGroupId: z.number({ required_error: 'Please select a group' }),
  product_attribute_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().optional(),
        description: z.string().optional(),
        language: z.string(),
      })
    )
    .transform((items, ctx) => {
      const filteredItems = items.filter(item => item.name?.trim() !== '')
      if (filteredItems.length === 0) {
        items.forEach((item, index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one language profile needs to be filled',
            path: [`[${index}].name`],
          })
        })
      }
      return filteredItems
    }),
}) satisfies z.ZodType<ProductAttributeFormValues>

interface ProductAttributeFormProps {
  data: {
    id: number | null
    defaultGroupId?: number
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function ProductAttributeForm({
  data,
  onDirtyChange,
  onRequestClose,
}: ProductAttributeFormProps) {
  const { allLanguages, languageId, language: currentLanguage } = useLanguage()
  const [id, setId] = useState(data?.id)
  const { showConfirmation } = useConfirmation()

  const {
    data: { findUniqueProductAttribute: attributeData } = {},
    loading: loadingData,
  } = useQuery(GET_PRODUCT_ATTRIBUTE, {
    variables: { id },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const { data: groupsData } = useQuery(GET_ATTRIBUTE_GROUPS_FOR_SELECT, {
    fetchPolicy: 'cache-and-network',
  })

  const availableGroups = groupsData?.findManyProductAttributeGroup || []

  const getGroupName = (group: any) => {
    return (
      group.product_attribute_group_profiles.find(
        (p: any) => p.language.toLowerCase() === currentLanguage.toLowerCase()
      )?.name ||
      group.product_attribute_group_profiles[0]?.name ||
      'Unknown Group'
    )
  }

  const form = useForm<ProductAttributeFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      code: '',
      productAttributeGroupId: undefined,
      product_attribute_profiles: [],
    },
  })

  const [updateAttribute] = useMutation(UPDATE_PRODUCT_ATTRIBUTE, {
    onError: () => {
      toast.error('Failed to update attribute')
    },
    onCompleted: () => {
      toast.success('Attribute updated successfully')
    },
    refetchQueries: ['GET_PRODUCT_ATTRIBUTES', 'GetProductAttribute', 'GET_ATTRIBUTES_TREE'],
  })

  const [deleteAttribute] = useMutation(DELETE_PRODUCT_ATTRIBUTE, {
    refetchQueries: ['GET_PRODUCT_ATTRIBUTES', 'GET_ATTRIBUTES_TREE'],
  })

  const handleDelete = () => {
    if (id) {
      const productCount = attributeData?.product_containers?.length || 0
      showConfirmation({
        onConfirm: async () => {
          try {
            await deleteAttribute({
              variables: { where: { id } },
            })
            toast.success('Attribute deleted successfully')
            onRequestClose?.()
          } catch (error) {
            toast.error('Failed to delete attribute')
          }
        },
        description: productCount
          ? `This attribute is used by ${productCount} product(s). Deleting it will remove it from those products. This action cannot be undone.`
          : 'Are you sure you want to delete this attribute? This action cannot be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      })
    }
  }

  const [createAttribute] = useMutation(CREATE_PRODUCT_ATTRIBUTE, {
    onError: () => {
      toast.error('Failed to create attribute')
    },
    onCompleted: response => {
      toast.success('Attribute created successfully')
      const newId = response.createProductAttribute.id
      setId(newId)
    },
    refetchQueries: ['GET_PRODUCT_ATTRIBUTES', 'GET_ATTRIBUTES_TREE'],
  })

  function onSubmit(values: ProductAttributeFormValues) {
    if (id) {
      updateAttribute({
        variables: {
          where: { id },
          data: {
            code: values.code,
            productAttributeGroupId: values.productAttributeGroupId,
            profiles: values.product_attribute_profiles.map(p => ({
              id: p.id,
              language: p.language,
              name: p.name || '',
              description: p.description,
            })),
          },
        },
      })
    } else {
      createAttribute({
        variables: {
          data: {
            code: values.code,
            productAttributeGroupId: values.productAttributeGroupId!,
            profiles: values.product_attribute_profiles.map(p => ({
              language: p.language,
              name: p.name || '',
              description: p.description,
            })),
          },
        },
      })
    }
  }

  useEffect(() => {
    if (!loadingData) {
      if (attributeData) {
        const existingProfiles =
          attributeData.product_attribute_profiles?.filter(
            (p: any) => p.language && allLanguages.includes(p.language)
          ) ?? []

        const allProfiles = allLanguages.map(lang => {
          const existingProfile = existingProfiles.find(
            (p: any) => p.language === lang
          )
          if (existingProfile) {
            return {
              id: existingProfile.id,
              name: existingProfile.name ?? '',
              description: existingProfile.description ?? '',
              language: existingProfile.language,
            }
          } else {
            return {
              name: '',
              description: '',
              language: lang,
            }
          }
        })

        form.reset({
          code: attributeData.code ?? '',
          productAttributeGroupId: attributeData.productAttributeGroupId,
          product_attribute_profiles: allProfiles,
        })
      } else {
        form.reset({
          code: '',
          productAttributeGroupId: data?.defaultGroupId ?? undefined,
          product_attribute_profiles: allLanguages.map(lang => ({
            name: '',
            description: '',
            language: lang,
          })),
        })
      }
    }
  }, [attributeData, loadingData, allLanguages, form.reset])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Product Attribute</SheetTitle>
          <LanguageSwitcher />
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <FormField
                control={form.control}
                name="productAttributeGroupId"
                render={({ field }) => (
                  <InputFieldContainer
                    label="Attribute Group"
                    description="Select the group this attribute belongs to."
                    control={form.control}
                    name="productAttributeGroupId"
                    render={() => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Select
                            onValueChange={val => field.onChange(Number(val))}
                            value={field.value?.toString()}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select a group" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableGroups.map((group: any) => (
                                <SelectItem
                                  key={group.id}
                                  value={group.id.toString()}
                                >
                                  {getGroupName(group)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              />

              <InputFieldContainer
                label="Attribute Name"
                description="This is the public display name."
                control={form.control}
                name={`product_attribute_profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Code"
                description="Unique identifier code for this attribute."
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="e.g. high-legs"
                        type="text"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Description"
                control={form.control}
                name={`product_attribute_profiles.${languageId}.description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Description"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-2">
                {id && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleDelete}
                    className="shrink-0"
                  >
                    <Icons.trash className="h-4 w-4 text-red-500" />
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={loadingData}
                  className="flex-1"
                >
                  {id ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
